/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * Admin auth service. Copied and trimmed from app-frontend's
 * `src/services/auth.ts`: the challenge → login JWT flow, token refresh, and
 * an `isAdmin()` gate. Customer-facing concerns (unsaved-vault confirm on
 * logout, registration) are dropped. Storage keys are admin-namespaced.
 */

import { z } from 'zod';

// --- Boundary schemas --------------------------------------------------------

const NonEmptyRefreshToken = z
  .string()
  .min(1, { error: 'refreshToken must be a non-empty string' });

const JwtTokenString = z
  .string()
  .min(1)
  .refine(t => t.split('.').length === 3, {
    error: issue =>
      `JWT must have exactly 3 dot-separated segments, got ${
        (issue.input as string).split('.').length
      }`,
  });

export const UserClaimsSchema = z
  .object({
    email: z.string().min(1),
    groups: z.array(z.string()).optional(),
  })
  .passthrough();
export type UserClaims = z.infer<typeof UserClaimsSchema>;

export const LoginResponseSchema = z.object({
  token: JwtTokenString,
  refreshToken: NonEmptyRefreshToken.optional(),
  claims: UserClaimsSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const RefreshResponseSchema = z.union([
  z.object({
    accessToken: JwtTokenString,
    refreshToken: NonEmptyRefreshToken.optional(),
  }),
  z
    .object({
      token: JwtTokenString,
      refreshToken: NonEmptyRefreshToken.optional(),
      claims: UserClaimsSchema.optional(),
    })
    .transform(({ token, refreshToken }) => ({
      accessToken: token,
      ...(refreshToken !== undefined ? { refreshToken } : {}),
    })),
]);
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const ChallengeResponseSchema = z
  .object({
    identifier: z.string().min(1),
    url: z.string().min(1),
  })
  .passthrough();
export type ChallengeResponse = z.infer<typeof ChallengeResponseSchema>;

export interface LoginPayload {
  email: string;
  key: string;
}

export type ChallengeFormResult =
  (ChallengeResponse & { ok: true }) | { ok: false; error: Error };

export class AuthResponseError extends Error {
  readonly reason:
    | 'malformed-login-response'
    | 'malformed-refresh-response'
    | 'malformed-challenge-response';
  readonly issues: z.core.$ZodIssue[];

  constructor(reason: AuthResponseError['reason'], zodError: z.ZodError) {
    super(`${reason}: ${zodError.message}`);
    this.name = 'AuthResponseError';
    this.reason = reason;
    this.issues = zodError.issues;
  }
}

export class InvalidTokenError extends Error {
  constructor(message: string = 'Invalid token format') {
    super(message);
    this.name = 'InvalidTokenError';
  }
}

export class MalformedTokenError extends Error {
  readonly reason: 'doubled-jwt' | 'missing-segments';
  readonly segmentCount: number;

  constructor(segmentCount: number) {
    const reason: MalformedTokenError['reason'] =
      segmentCount > 3 ? 'doubled-jwt' : 'missing-segments';
    super(
      `MalformedTokenError (${reason}): JWT must have exactly 3 dot-separated segments, got ${segmentCount}`
    );
    this.name = 'MalformedTokenError';
    this.reason = reason;
    this.segmentCount = segmentCount;
  }
}

export class RefreshEndpointMissingError extends Error {
  constructor() {
    super(
      'Refresh endpoint not available (backend predates /api/auth/refresh)'
    );
    this.name = 'RefreshEndpointMissingError';
  }
}

const parseLoginResponse = (raw: unknown): LoginResponse => {
  const result = LoginResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-login-response', result.error);
  }
  return result.data;
};

const parseRefreshResponse = (raw: unknown): RefreshResponse => {
  const result = RefreshResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-refresh-response', result.error);
  }
  return result.data;
};

const parseChallengeResponse = (raw: unknown): ChallengeResponse => {
  const result = ChallengeResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-challenge-response', result.error);
  }
  return result.data;
};

// --- AuthService -------------------------------------------------------------

export class AuthService {
  private readonly refreshTokenKey = 'bd_admin_refresh_token';
  private readonly refreshHintKey = 'bd_admin_refresh_hint';

  private accessToken: string | null = null;
  private user: UserClaims | null = null;

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  setAccessToken(token: string): void {
    this.assertWellFormedJwt(token);
    const payload = this.decodeToken(token);
    this.setUser(payload);
    this.accessToken = token;
  }

  private assertWellFormedJwt(token: string): void {
    const segmentCount = token.split('.').length;
    if (segmentCount !== 3) {
      throw new MalformedTokenError(segmentCount);
    }
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  private persistRefreshTokenFrom(refreshToken: string | undefined): void {
    if (refreshToken !== undefined) {
      this.setRefreshToken(refreshToken);
    }
  }

  removeTokens(): void {
    this.accessToken = null;
    localStorage.removeItem(this.refreshTokenKey);
  }

  getUser(): UserClaims | null {
    return this.user;
  }

  getRefreshHintEmail(): string | null {
    return localStorage.getItem(this.refreshHintKey);
  }

  getEmail(): string | undefined {
    return this.getUser()?.email;
  }

  isAdmin(): boolean {
    return this.getUser()?.groups?.includes('admin') ?? false;
  }

  setUser(user: UserClaims): void {
    this.user = user;
    try {
      localStorage.setItem(this.refreshHintKey, user.email);
    } catch {
      /* storage unavailable — hint is an optimisation, not a dependency */
    }
  }

  removeUser(): void {
    this.user = null;
    localStorage.removeItem(this.refreshHintKey);
  }

  async isAuthenticated(): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) return false;

    let payload: UserClaims & { exp?: unknown };
    try {
      payload = this.decodeToken(token);
    } catch (error) {
      console.error('Token validation error:', error);
      this.removeTokens();
      this.removeUser();
      return false;
    }

    const currentTime = Date.now() / 1000;
    if (typeof payload.exp === 'number' && payload.exp <= currentTime) {
      try {
        await this.refreshAccessToken();
        return true;
      } catch (error) {
        console.error('Token refresh failed during isAuthenticated:', error);
        return false;
      }
    }
    return true;
  }

  decodeToken(token: string): UserClaims {
    try {
      const segments = token.split('.');
      const base64Url = segments[1];
      if (!base64Url) throw new Error('missing payload segment');
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return UserClaimsSchema.parse(JSON.parse(jsonPayload));
    } catch {
      throw new InvalidTokenError();
    }
  }

  async login(payload: LoginPayload, url: string): Promise<LoginResponse> {
    const response = await fetch(`/api/auth/login/${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Login failed ${response.status}: ${response.statusText}`
      );
    }

    const data = parseLoginResponse(await response.json());
    this.setAccessToken(data.token);
    this.persistRefreshTokenFrom(data.refreshToken);
    this.setUser(data.claims);
    return data;
  }

  async requestChallenge(email: string): Promise<ChallengeResponse> {
    const response = await fetch('/api/auth/requestchallenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw new Error(
        `Challenge request failed ${response.status}: ${response.statusText}`
      );
    }
    return parseChallengeResponse(await response.json());
  }

  async refreshAccessToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const currentEmail = this.getUser()?.email ?? this.getRefreshHintEmail();
    if (!currentEmail) {
      throw new Error('No stored user email available for refresh');
    }

    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
      body: JSON.stringify({ email: currentEmail, refreshToken }),
    });

    if (response.status === 404) {
      this.removeTokens();
      this.removeUser();
      throw new RefreshEndpointMissingError();
    }

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = parseRefreshResponse(await response.json());
    this.setAccessToken(data.accessToken);
    this.persistRefreshTokenFrom(data.refreshToken);
    return data.accessToken;
  }

  logout(): void {
    this.removeTokens();
    this.removeUser();
    window.location.href = '/';
  }

  async challengeLogin(
    code: string,
    challengeResult: ChallengeResponse,
    errorMessage: HTMLElement,
    submitBtn: HTMLButtonElement
  ): Promise<boolean> {
    try {
      submitBtn.textContent = 'Signing in...';
      submitBtn.disabled = true;
      errorMessage.hidden = true;

      const loginData: LoginPayload = {
        email: challengeResult.identifier,
        key: code,
      };
      await this.login(loginData, challengeResult.url);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Login error:', error, message);
      errorMessage.textContent = `Login failed. ${message}`;
      errorMessage.hidden = false;
      submitBtn.textContent = 'Sign In';
      submitBtn.disabled = false;
      return false;
    }
    return true;
  }

  async formChallenge(
    form: HTMLElement,
    errorMessage: HTMLElement
  ): Promise<ChallengeFormResult> {
    const submitBtn = form.querySelector(
      'wa-button'
    ) as HTMLButtonElement | null;
    try {
      if (submitBtn) {
        submitBtn.textContent = 'Requesting code...';
        submitBtn.disabled = true;
      }
      errorMessage.hidden = true;

      const emailInput = form.querySelector('wa-input[name="email"]') as
        (HTMLElement & { value?: string }) | null;
      const email = emailInput?.value ?? '';

      const result = await this.requestChallenge(email);
      return { ...result, ok: true };
    } catch (error) {
      console.error('Challenge error:', error);
      errorMessage.textContent = 'Challenge failed. Please try again.';
      errorMessage.hidden = false;
      if (submitBtn) {
        submitBtn.textContent = 'Request Code';
        submitBtn.disabled = false;
      }
      return {
        ok: false,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }
}

export const authService = new AuthService();
